import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineUser,
  HiOutlineUsers,
  HiOutlineBuildingOffice,
  HiOutlineChevronDown,
  HiUserPlus,
} from "react-icons/hi2";

type AuthType = {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  description?: string;
};

const AuthSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const authTypes: AuthType[] = [
    {
      id: "user",
      label: t("auth.userLogin"),
      path: "/auth/sign-in",
      icon: <HiOutlineUser className="h-4 w-4" />,
      description: t("auth.userLoginDesc"),
    },
    {
      id: "responder",
      label: t("auth.responderLoginTitle"),
      path: "/auth/responder-login",
      icon: <HiOutlineUsers className="h-4 w-4" />,
      description: t("auth.responderLoginDesc"),
    },
    {
      id: "organization",
      label: t("auth.organizationLoginTitle"),
      path: "/auth/organization-login",
      icon: <HiOutlineBuildingOffice className="h-4 w-4" />,
      description: t("auth.organizationLoginDesc"),
    },
    {
      id: "new",
      label: t("auth.signupTitle"),
      path: "/auth/sign-up",
      icon: <HiUserPlus className="h-4 w-4" />,
      description: t("auth.signupDesc"),
    },
  ];

  const currentAuth =
    authTypes.find((type) =>
      location.pathname.includes(type.path.split("/").pop() || "")
    ) || authTypes[0];

  const handleSelect = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(!isOpen)}
        className="group flex w-full items-center justify-between rounded-xl border-2 border-brand-200 bg-gradient-to-r from-brand-50 to-blue-50 px-5 py-4 text-sm font-medium shadow-lg shadow-brand-200/30 transition-all hover:border-brand-400 hover:shadow-xl hover:shadow-brand-300/40 dark:border-brand-700/50 dark:from-brand-900/40 dark:to-navy-800 dark:shadow-brand-900/30 dark:hover:border-brand-500 dark:hover:shadow-brand-800/40"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-md shadow-brand-300/40 group-hover:shadow-lg group-hover:shadow-brand-400/50 dark:from-brand-600 dark:to-brand-700">
            {currentAuth.icon}
          </div>
          <div className="flex flex-col items-start">
            <span className="text-base font-semibold text-gray-800 dark:text-white">
              {currentAuth.label}
            </span>
            {currentAuth.description && (
              <span className="text-xs text-gray-700 dark:text-gray-400">
                {currentAuth.description}
              </span>
            )}
          </div>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="rounded-full bg-brand-100 p-1 text-brand-600 dark:bg-brand-900/40 dark:text-brand-400"
        >
          <HiOutlineChevronDown className="h-5 w-5" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border-2 border-brand-200/50 bg-white shadow-2xl shadow-brand-200/20 dark:border-brand-700/30 dark:bg-navy-800 dark:shadow-brand-900/30"
          >
            {authTypes.map((type) => {
              const isActive = location.pathname.includes(
                type.path.split("/").pop() || ""
              );
              return (
                <motion.button
                  key={type.id}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelect(type.path)}
                  className={`flex w-full items-center gap-3 px-5 py-4 text-left transition-all ${
                    isActive
                      ? "to-transparent bg-gradient-to-r from-brand-100/80 dark:from-brand-900/30"
                      : "hover:bg-gray-50 dark:hover:bg-navy-700/50"
                  }`}
                >
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-xl transition-all ${
                      isActive
                        ? "bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-md shadow-brand-300/40"
                        : "bg-gray-100 text-gray-500 dark:bg-navy-700 dark:text-gray-400"
                    }`}
                  >
                    {type.icon}
                  </div>
                  <div className="flex flex-1 flex-col items-start">
                    <span
                      className={`text-sm font-semibold ${
                        isActive
                          ? "text-brand-700 dark:text-brand-300"
                          : "text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {type.label}
                    </span>
                    {type.description && (
                      <span className="text-xs text-gray-700 dark:text-gray-400">
                        {type.description}
                      </span>
                    )}
                  </div>
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="h-2 w-2 rounded-full bg-brand-500 shadow-lg shadow-brand-300/50 dark:bg-brand-400"
                    />
                  )}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AuthSwitcher;
